import React from 'react';
import {
  Chip,
  Stack,
  Typography,
} from '@mui/material';

export const statusChip = (status?: string) => {
  const s = String(status || '').toLowerCase();

  let color: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default';

  if (s === 'succeeded' || s === 'publishable' || s === 'passed') color = 'success';
  if (s === 'pending' || s === 'running' || s === 'needs_review') color = 'warning';
  if (s === 'failed' || s === 'validation_failed' || s === 'error') color = 'error';
  if (s === 'queued' || s === 'created') color = 'info';

  return <Chip size="small" color={color} variant="outlined" label={status || 'unknown'} />;
};

export function valueOf(...values: any[]) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
}

export function versionLabel(run: any) {
  return valueOf(
    run?.product_version_label,
    run?.product_version_name,
    run?.product_version,
    run?.product_version_id,
    run?.prompt_inputs?.product_version,
    run?.prompt_inputs?.product_version_id,
    run?.prompt_inputs?.version_context?.product_version,
    run?.prompt_inputs?.version_context?.product_version_id,
    run?.doc_version,
  ) || '-';
}

export function deploymentLabel(run: any) {
  return valueOf(
    run?.deployment_scope,
    run?.deployment,
    run?.scope,
    run?.prompt_inputs?.deployment_scope,
    run?.prompt_inputs?.version_context?.deployment_scope,
  ) || '-';
}

export function versionSourceLabel(run: any) {
  return valueOf(
    run?.version_source,
    run?.version_context_source,
    run?.prompt_inputs?.version_source,
    run?.prompt_inputs?.version_context?.version_source,
  ) || '-';
}

export function docVersionLabel(run: any) {
  return valueOf(
    run?.doc_version,
    run?.documentation_version,
    run?.prompt_inputs?.doc_version,
    run?.prompt_inputs?.version_context?.doc_version,
  ) || '-';
}

export function safeJsonParse(value: any): any {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

export function stringifyValue(value: any, fallback = '-') {
  if (value === null || value === undefined || value === '') return fallback;

  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function stringifyForMarkdown(value: any): string {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'string') {
    return value;
  }

  try {
    return `\`\`\`json\n${JSON.stringify(value, null, 2)}\n\`\`\``;
  } catch {
    return String(value);
  }
}

export function markdownFromListItems(value: any): string {
  if (!Array.isArray(value) || value.length === 0) return '';

  return value
    .map((item, index) => {
      if (typeof item === 'string') {
        return `### Item ${index + 1}\n\n${item}`;
      }

      if (item && typeof item === 'object') {
        const parts: string[] = [`### Item ${index + 1}`];

        if (item.source) parts.push(`**Source:** ${item.source}`);
        if (item.id) parts.push(`**ID:** ${item.id}`);
        if (item.title) parts.push(`**Title:** ${item.title}`);
        if (item.quote) parts.push(`\n${String(item.quote)}`);

        const remaining = { ...item };
        delete remaining.source;
        delete remaining.id;
        delete remaining.title;
        delete remaining.quote;

        if (Object.keys(remaining).length) {
          parts.push(`\n\`\`\`json\n${JSON.stringify(remaining, null, 2)}\n\`\`\``);
        }

        return parts.join('\n\n');
      }

      return `### Item ${index + 1}\n\n${String(item)}`;
    })
    .join('\n\n---\n\n');
}

export function getPromptInputs(run: any) {
  return safeJsonParse(run?.prompt_inputs) || {};
}

export function getResponseMetadata(run: any) {
  return safeJsonParse(run?.response_metadata) || {};
}

export function getRagChunks(run: any): any[] {
  const inputs = getPromptInputs(run);
  const metadata = getResponseMetadata(run);
  const raw =
    run?.rag_chunks ||
    run?.rag_chunks_json ||
    run?.retrieved_chunks ||
    inputs?.rag_chunks ||
    metadata?.rag?.chunks ||
    metadata?.rag_chunks ||
    [];

  const parsed = safeJsonParse(raw);
  return Array.isArray(parsed) ? parsed : [];
}

export function countWords(text?: string | null) {
  if (!text) return 0;
  return String(text).trim().split(/\s+/).filter(Boolean).length;
}

export const VersionScopeStack: React.FC<{ run: any }> = ({ run }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption">
      Version: <b>{versionLabel(run)}</b>
    </Typography>

    <Typography variant="caption" color="text.secondary">
      Doc: {docVersionLabel(run)}
    </Typography>

    <Typography variant="caption" color="text.secondary">
      Deployment: {deploymentLabel(run)}
    </Typography>

    <Typography variant="caption" color="text.secondary">
      Source: {versionSourceLabel(run)}
    </Typography>
  </Stack>
);